/**
 * 登录页面状态管理 Store
 *
 * 管理登录/注册/忘记密码/资料编辑等登录页面的视图与表单状态。
 * 从 uiStore.ts 拆分而来（P2-1 巨型 Store 拆解）。
 *
 * @module shared/stores/loginPageStore
 */

import { defineStore } from "pinia";
import { reactive } from "vue";

// ==================== 类型定义 ====================

/** 登录页面视图类型 */
export type LoginView = "login" | "register" | "forgot-password" | "profile";

/** 登录表单状态类型 */
export interface LoginFormState {
  isSubmitting: boolean;
  rememberMe: boolean;
  showPassword: boolean;
}

/** 资料编辑状态类型 */
export interface ProfileEditState {
  isEditing: boolean;
  hasChanges: boolean;
  isSaving: boolean;
}

/** 帮助联系状态类型 */
export interface HelpContactState {
  showHelp: boolean;
  showContact: boolean;
  selectedHelpTopic: string | null;
}

/** 登录页面状态类型 */
export interface LoginPageState {
  currentView: LoginView;
  loginForm: LoginFormState;
  profileEdit: ProfileEditState;
  helpContact: HelpContactState;
}

/** 对话框类型 */
export type DialogType = "help" | "contact" | "profile";

// ==================== Store 定义 ====================

export const useLoginPageStore = defineStore("loginPage", () => {
  // ==================== 登录页面状态 ====================

  const loginPageState = reactive<LoginPageState>({
    // 当前显示的视图
    currentView: "login",

    // 登录表单状态
    loginForm: {
      isSubmitting: false,
      rememberMe: false,
      showPassword: false,
    },

    // 资料编辑状态
    profileEdit: {
      isEditing: false,
      hasChanges: false,
      isSaving: false,
    },

    // 帮助和联系状态
    helpContact: {
      showHelp: false,
      showContact: false,
      selectedHelpTopic: null,
    },
  });

  // ==================== 登录页面操作方法 ====================

  /** 设置登录页面视图 */
  const setLoginView = (view: LoginView): void => {
    loginPageState.currentView = view;
  };

  /** 设置登录表单状态 */
  const setLoginFormState = (state: Partial<LoginFormState>): void => {
    Object.assign(loginPageState.loginForm, state);
  };

  /** 设置资料编辑状态 */
  const setProfileEditState = (state: Partial<ProfileEditState>): void => {
    Object.assign(loginPageState.profileEdit, state);
  };

  /** 设置帮助联系状态 */
  const setHelpContactState = (state: Partial<HelpContactState>): void => {
    Object.assign(loginPageState.helpContact, state);
  };

  /** 重置登录页面状态 */
  const resetLoginPageState = (): void => {
    loginPageState.currentView = "login";
    loginPageState.loginForm = {
      isSubmitting: false,
      rememberMe: false,
      showPassword: false,
    };
    loginPageState.profileEdit = {
      isEditing: false,
      hasChanges: false,
      isSaving: false,
    };
    loginPageState.helpContact = {
      showHelp: false,
      showContact: false,
      selectedHelpTopic: null,
    };
  };

  return {
    loginPageState,
    setLoginView,
    setLoginFormState,
    setProfileEditState,
    setHelpContactState,
    resetLoginPageState,
  };
});
